/** @jsxImportSource theme-ui */

import { fetchBepiForLocation } from 'bepi/bepi.saga';
import bepiSelectors from 'bepi/bepi.selectors';
import React, { useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import { InsightsState } from 'app-redux/InsightsState';
import * as dto from 'client/dto';

import { BdLocationVulnerabilityIcon } from 'components/_common/Icon/BdLocationVulnerabilityIcon';
import { Typography } from 'components/_common/Typography';

import locationsSelectors from 'locations/locations.selectors';

import BepiDisclaimer from './BepiDisclaimer';
import BepiScoreBar from './BepiScoreBar';
import BepiVulnerabilityStatement from './BepiVulnerabilityStatement';

type BepiDetailsProps = {
  locationId: number;
};

const BepiDetails: React.FC<BepiDetailsProps> = ({ locationId }) => {
  const dispatch = useDispatch();
  useEffect(() => {
    dispatch(fetchBepiForLocation(locationId));
  }, []);

  const locationName: string = useSelector<InsightsState, string>(
    locationsSelectors.getLocationName(locationId)
  );
  const bepiLocationName: string = useSelector<InsightsState, string>(
    bepiSelectors.getBepiLocationName(locationId)
  );
  const bepiValues: Record<dto.BepiCategory, number> = useSelector<
    InsightsState,
    Record<dto.BepiCategory, number>
  >(bepiSelectors.bepiValuesByLocationId(locationId));
  const locationResiliency = useSelector(
    bepiSelectors.getVulnerabilityIndexByLocationId(locationId)
  );

  const firstColumnBepiCategories: dto.BepiCategory[] = [
    dto.BepiCategory.Demographics,
    dto.BepiCategory.Healthcare,
    dto.BepiCategory.PublicHealth,
    dto.BepiCategory.DiseaseDynamics,
  ];
  const secondColumnBepiCategories: dto.BepiCategory[] = [
    dto.BepiCategory.PoliticalDomestic,
    dto.BepiCategory.PoliticalInternational,
    dto.BepiCategory.Economy,
  ];
  const bepiPanelStyle = {
    width: '100%',
    paddingTop: ['24px', '32px'],
  };

  return (
    <div className="bepi-score-container" sx={bepiPanelStyle}>
      <Typography variant="subtitle1" color="stone90" marginBottom="24px">
        Disease Risk Vulnerability in {locationName}
      </Typography>
      <div sx={{ marginBottom: '16px' }}>
        <BdLocationVulnerabilityIcon
          locationResiliency={locationResiliency}
          locationName={bepiLocationName}
        />
      </div>
      {locationResiliency !== null && (
        <div>
          <BepiVulnerabilityStatement
            sx={{ mb: '24px' }}
            bepiLocationName={bepiLocationName}
            locationResiliency={locationResiliency}
          />
          <div sx={{ display: [null, 'flex'], marginBottom: ['0', '8px'] }}>
            <div sx={{ width: ['100%', '50%'], marginRight: ['0', '24px'] }}>
              {firstColumnBepiCategories.map((category, index) => (
                <BepiScoreBar key={index} category={category} value={bepiValues?.[category]} />
              ))}
            </div>
            <div sx={{ width: ['100%', '50%'], marginLeft: ['0', '24px'] }}>
              {secondColumnBepiCategories.map((category, index) => (
                <BepiScoreBar key={index} category={category} value={bepiValues?.[category]} />
              ))}
            </div>
          </div>
        </div>
      )}
      <BepiDisclaimer />
    </div>
  );
};

export default BepiDetails;
